import React, {useEffect, useState} from 'react';
import {useParams, useLocation} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {hospitalDetailsSelector, hospitalDetailsLoadingSelector, patientDashboardSuccessState, patientDashboardFailureState, hospitalBookingModalState} from '@/services/redux/selectors';
import { HospitalProfileView as HospitalProfileViewComponent } from '@/components';
import { getHospitalDetailsRequest, resetPatientDashboardMessage, setHospitalBookingModalState, updateHospitalBookingModalState, clearHospitalBookingModalState } from '@/services/redux/slices';
import { HospitalBookingModal } from '@/components/specialist-screen';

export const HospitalProfileView: React.FC = () => {
  // =======================================
  // hooks
  // =======================================
  const dispatch = useDispatch();
  const {id} = useParams();
  const location = useLocation();

  // =======================================
  // selectors
  // =======================================
  const hospitalDetails = useSelector(hospitalDetailsSelector);
  const isLoading = useSelector(hospitalDetailsLoadingSelector);
  const savedModalState = useSelector(hospitalBookingModalState);

  // =======================================
  // states
  // =======================================
  const hospitalId = parseInt(id as string);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  // =======================================
  // functions
  // =======================================
  function handleBookNow() {
    setModalOpen(true);
    if (hospitalDetails) {
      dispatch(
        setHospitalBookingModalState({
          returnPath: location.pathname,
          hospitalId: hospitalId,
          hospitalName: hospitalDetails.name,
          specializations: hospitalDetails.specializations
            .filter(s => s.status)
            .map(s => ({id: s.id, label: s.label})),
          selectedSpecialization: null,
          isStep2: false,
        }),
      );
    }
  }
  
  // =======================================
  // side effect
  // =======================================
  useEffect(() => {
    dispatch(getHospitalDetailsRequest({id: hospitalId}));
  }, [hospitalId]);

  // Restore modal when returning from specialist profile (back button)
  useEffect(() => {
    if (
      hospitalDetails &&
      savedModalState &&
      savedModalState.returnPath === location.pathname &&
      savedModalState.hospitalId === hospitalId
    ) {
      setModalOpen(true);
    }
  }, [hospitalDetails, savedModalState, location.pathname, hospitalId]);


  if (isNaN(hospitalId)) {
    return <div>Invalid hospital ID</div>;
  }
  if (isLoading) {
    return <div>Loading...</div>;
  }
  if (!hospitalDetails) {
    return <div>Hospital not found</div>;
  }

  return (
    <div className="p-3 sm:p-6 md:p-8 flex grow">
      <div className="hospital-profile-container bg-white grow rounded-2xl border border-ui_input_border p-3 sm:p-4 md:p-6 gap-4 flex flex-col">
        <HospitalProfileViewComponent profileData={hospitalDetails} viewOnly={true} handleBookNow={handleBookNow} />
      </div>

      <HospitalBookingModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          dispatch(clearHospitalBookingModalState());
        }}
        hospitalName={hospitalDetails.name}
        specializations={hospitalDetails.specializations.filter(specialization => specialization.status).map(specialization => ({
          id: specialization.id,
          label: specialization.label,
        }))}
        hospitalId={hospitalId}
        initialSelectedSpecialization={
          savedModalState?.returnPath === location.pathname ? savedModalState?.selectedSpecialization ?? null : null
        }
        initialIsStep2={savedModalState?.returnPath === location.pathname ? savedModalState?.isStep2 : false}
        onStateChange={data =>
          dispatch(
            updateHospitalBookingModalState({
              selectedSpecialization: data.selectedSpecialization,
              isStep2: data.isStep2,
            }),
          )
        }
      />
    </div>
  );
};
